import { useState } from "react";
import { errorMessage } from "../api/client";
import type { WalletUnit } from "../api/endpoints";
import { useWalletHistoryQuery, useWalletsQuery } from "../api/queries";
import { formatDateTime } from "../lib/format";
import { formatCoins, formatCredit, formatSignedAmount } from "../lib/wallet";
import { selectIsGuest, useTownStore } from "../state/store";
import { Dialog } from "./Dialog";
import { CoinIcon, CreditIcon } from "./WalletChips";

/**
 * Wallet dialog: Coins and Townsy Credit side by side as two separate ledgers (docs/04 section 1),
 * each with its own recent history. Honest earning copy comes from the server
 * (`WalletsResponse.earning`); nothing here can add, convert or withdraw anything.
 */
export function WalletPanel() {
  const open = useTownStore((s) => s.ui.walletOpen);
  const setUi = useTownStore((s) => s.setUi);
  const isGuest = useTownStore(selectIsGuest);
  const [tab, setTab] = useState<WalletUnit>("COIN");
  const wallets = useWalletsQuery(open && !isGuest);
  const history = useWalletHistoryQuery(tab, open && !isGuest);
  if (!open || isGuest) return null;
  const close = () => setUi({ walletOpen: false });
  const w = wallets.data;
  const items = history.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Dialog title="Your wallet" onClose={close} describedBy="wallet-explainer">
      <div className="stack" data-testid="wallet-panel">
        {wallets.error ? (
          <div className="notice notice-danger" role="alert">
            Couldn't load your balances: {errorMessage(wallets.error)}{" "}
            <button type="button" className="btn btn-sm" onClick={() => void wallets.refetch()}>
              Retry
            </button>
          </div>
        ) : null}
        <div className="wallet-balances">
          <section className="wallet-balance wallet-balance-coin" aria-label="Coins">
            <div className="wallet-balance-label">
              <CoinIcon /> Coins
            </div>
            <div className="wallet-balance-value" data-testid="wallet-coins">
              {w ? formatCoins(w.coins.available) : "…"}
            </div>
            {w && w.coins.reserved > 0 ? <div className="small muted">{formatCoins(w.coins.reserved)} reserved for a pending order</div> : null}
            <p className="hint">Spent on homes and items. Not money, not transferable, never exchanged for Credit or cash.</p>
          </section>
          <section className="wallet-balance wallet-balance-credit" aria-label="Townsy Credit">
            <div className="wallet-balance-label">
              <CreditIcon /> Townsy Credit
            </div>
            <div className="wallet-balance-value" data-testid="wallet-credit">
              {w ? formatCredit(w.credit.availableCents) : "…"}
            </div>
            {w && w.credit.reservedCents > 0 ? <div className="small muted">{formatCredit(w.credit.reservedCents)} reserved for a pending order</div> : null}
            <p className="hint">
              Returned when another member takes over a commercial building you own. Usable on eligible dollar purchases; not cash and
              cannot be withdrawn.{" "}
              <a href="/legal/payments" target="_blank" rel="noopener">
                Payments &amp; Credit Policy
              </a>
            </p>
          </section>
        </div>
        <p id="wallet-explainer" className={`notice ${w?.earning.available ? "notice-info" : ""}`} style={{ margin: 0 }} data-testid="wallet-earning">
          {w ? w.earning.message : "Coins are earned from sponsored tasks in the shops."}
        </p>
        <div className="row">
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setUi({ walletOpen: false, earnOpen: true })} data-testid="wallet-earn">
            Earn coins: see sponsored tasks
          </button>
        </div>

        <div className="section">
          <div className="tabs" role="tablist" aria-label="History">
            <button type="button" role="tab" className="tab" aria-selected={tab === "COIN"} onClick={() => setTab("COIN")}>
              Coin history
            </button>
            <button type="button" role="tab" className="tab" aria-selected={tab === "CREDIT_USD"} onClick={() => setTab("CREDIT_USD")}>
              Credit history
            </button>
          </div>
          {history.isPending ? (
            <p className="muted small">Loading history…</p>
          ) : history.error ? (
            <p className="small error-text">
              Couldn't load the history: {errorMessage(history.error)}{" "}
              <button type="button" className="btn btn-sm" onClick={() => void history.refetch()}>
                Retry
              </button>
            </p>
          ) : items.length === 0 ? (
            <p className="muted small">{tab === "COIN" ? "No coin activity yet." : "No credit activity yet."}</p>
          ) : (
            <ul className="history-list wallet-history" data-testid="wallet-history">
              {items.map((it) => (
                <li key={`${it.journalId}-${it.unit}`}>
                  <span className={`wallet-amount ${it.amount < 0 ? "neg" : "pos"}`}>{formatSignedAmount(it.unit, it.amount)}</span>
                  <span style={{ minWidth: 0, flex: 1 }}>{it.description}</span>
                  <span className="small muted nowrap" title={it.at}>
                    {formatDateTime(it.at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {history.hasNextPage ? (
            <button type="button" className="btn btn-sm" disabled={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
              {history.isFetchingNextPage ? "Loading…" : "Load older"}
            </button>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
